import React from 'react';
import styled from 'styled-components';
import { Grid, Type } from '../../../app/StyleGuide';
import StepIcons from '../../../components/StepIcons';
import windowService from '../../../services/windowService';
import { Task } from '../models/task';
import { CourseResponse } from '../models/courseResponse';
import { ListItemFlexRow } from '../../../components/ListItemFlexRow';
import ConfirmationModal from '../../../components/ConfirmationModal';
import useModal from '../../../hooks/useModal';
import { NextAction } from '../models/nextAction';
import VideoTaskPreviewModal from './VideoTaskPreviewModal';
import EmptyState from '../../../components/EmptyState';
import { TrainingCourse } from '../models/trainingCourse';
import { useUser } from '../../authentication';
import { getRedirectToLoginFromLoggedOutState } from '../api/getRedirectToLoginFromLoggedOutState';
import { UserTrainingAccess } from '../models/UserTrainingAccess';
import getTrainingAction from '../utils/getTrainingAction';

const Steps = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Step = styled(ListItemFlexRow)`
  padding: ${Grid._5};
  font-size: ${Type.Scale._3};
  font-weight: ${Type.Weight.semibold};

  &[disabled] {
    cursor: not-allowed;
  }

  span {
    flex: 1;

    &:hover {
      text-decoration: ${props => (props.disabled ? 'none' : 'underline')};
    }
  }

  div {
    display: flex;
    align-items: center;
    gap: ${Grid._4};
  }
`;

const CourseStep = ({ step, userCanAccess, onClick }: { step: Task; userCanAccess: boolean; onClick: () => void }) => {
  return (
    <Step key={step._id.id} disabled={!userCanAccess} onClick={onClick}>
      <div>
        <i className={`step-icon ${StepIcons[step._type as keyof typeof StepIcons]}`} />
        <span>{step.taskName}</span>
      </div>
    </Step>
  );
};

export default function CourseSteps({
  course,
  trainingId,
  nextAction,
  isWithinPathway,
  userAccess,
}: {
  course: TrainingCourse | CourseResponse;
  trainingId: string;
  nextAction?: NextAction;
  isWithinPathway?: boolean;
  userAccess: UserTrainingAccess;
}) {
  const user = useUser();
  const { hasPaidForCourse } = userAccess;

  const [modal, openModal] = useModal((type: string, payload: Task, dismissModal: () => void) => {
    const redirectToPurchase = () => windowService.redirectToPurchase(course.itemNumber);
    const trainingType = isWithinPathway ? 'pathway' : 'course';
    const { trainingActionText, handleRedirect } = getTrainingAction(trainingType, trainingId, nextAction);

    switch (type) {
      case 'unlockTraining':
        return (
          <ConfirmationModal
            buttonActionText="Get Started"
            buttonType="primary"
            title="Unlock Training"
            prompt="Oh no! It looks like you don't have a subscription. Let's fix that so that you can train and develop others."
            handleSubmit={redirectToPurchase}
            handleDismiss={dismissModal}
          />
        );
      case 'startTraining':
        return (
          <ConfirmationModal
            buttonActionText={trainingActionText}
            buttonType="primary"
            title={trainingActionText}
            prompt="You'll need to start this assignment to view this step. Would you like to begin this training?"
            handleSubmit={handleRedirect}
            handleDismiss={dismissModal}
          />
        );
      case 'videoPreview':
        return (
          <VideoTaskPreviewModal
            task={payload}
            buttonActionText={trainingActionText}
            handleSubmit={handleRedirect}
            handleDismiss={dismissModal}
          />
        );
      default:
        return null;
    }
  });

  const handleStepClick = (step: Task) => {
    if (!user) {
      getRedirectToLoginFromLoggedOutState(
        window.location.href,
        '?route=' + encodeURIComponent(window.location.pathname.substring(1))
      ).then(redirect => {
        if (window.getIsSSO()) {
          windowService.openUrlInSameTab(redirect);
        }
      });
    } else if (!hasPaidForCourse) {
      openModal('unlockTraining');
    } else if (step._type === 'ThirdPartyVideo' || step._type === 'Video') {
      openModal('videoPreview', step);
    } else {
      openModal('startTraining');
    }
  };
  if (course.tasks.length === 0) {
    return <EmptyState title="No steps found" />;
  }
  return (
    <Steps>
      {course.tasks.map(step => (
        <CourseStep
          key={step._id.id}
          step={step}
          userCanAccess={hasPaidForCourse}
          onClick={() => handleStepClick(step)}
        />
      ))}
      {modal}
    </Steps>
  );
}
